import * as _ from 'underscore';
import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { watchWithUndo } from '../../common/utilities/WatchWithUndo';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '../../settings/interfaces/ISiteSettingsForSiteAdmin';
import { ActionContext } from '../helpers/ActionContext';
import { Module } from '../Module';
import { SiteUsers } from '../resources/SiteUsers';
import {
  IUpdateSiteSettingsForSiteAdminParams,
  UpdateSiteSettingsForSiteAdminRequest,
} from '../ServerApi/requests/UpdateSiteSettingsForSiteAdminRequest';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { AddUsersOptionsAction } from '../services/AddUsersOptionsAction';
import { ConfirmActionDialog } from '../services/ConfirmActionDialog';
import { HelpService } from '../services/HelpService';
import { ServerService } from '../services/ServerService';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../services/SiteAuthenticationSettingsService';
import { ToasterService, ToastType } from '../services/ToasterService';
import { WindowLocationService } from '../services/WindowLocationService';

interface IActionDialogSiteAuthenticationProviderEnableScope extends ng.IScope {
  aboutToBeDisabled: IAuthenticationProvider[];
  aboutToLockMyselfOut: boolean;
}

export interface ISiteAuthenticationSettingsCtrlScope extends ng.IScope {
  download: (evt: Event, url: string) => void;
  enableProvider: (id: string) => ng.IPromise<void>;
  getHelp: (category?: string) => void;
  getProviderDescription: (provider: IAuthenticationProvider) => string;
  getProviderDisplayName: (provider: IAuthenticationProvider) => string;
  isConnectedClientsVisible: boolean;
  isEmbeddedVizForSiteSamlSupported: boolean;
  isExternalProviderEnabled: boolean;
  isExternalProviderEnabledChanged: () => void;
  isTableauIdSupported: boolean;
  openAddExternalUsersDialog: ($event: JQueryEventObject) => void;
  siteAuthenticationSettings: ISiteAuthenticationSettings;
  siteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin;
  usersUrl: string;
}

// controller for siteAuthenticationSettings.pug
export class SiteAuthenticationSettingsCtrl {
  public static $inject = [
    '$q',
    '$scope',
    '$translate',
    '$window',
    'AddUsersOptionsAction',
    'ConfirmActionDialog',
    'HelpService',
    'ServerService',
    'SiteAuthenticationSettingsService',
    'SiteUsers',
    'ToasterService',
    'WindowLocationService',
  ];

  constructor(
    private $q: ng.IQService,
    private $scope: ISiteAuthenticationSettingsCtrlScope,
    private $translate: ng.translate.ITranslateService,
    private $window: ng.IWindowService,
            addUsersOptionsAction: AddUsersOptionsAction,
    private confirmActionDialog: ConfirmActionDialog,
    private help: HelpService,
    private server: ServerService,
            siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
    private siteUsers: SiteUsers,
    private toaster: ToasterService,
            windowLocation: WindowLocationService
  ) {
    $scope.download = (evt: Event, url: string) => {
      evt.preventDefault();
      windowLocation.download(url);
    };
    $scope.enableProvider = (id: string) => {
      return this.showEnableProviderWarning(id)
        .then(() => siteAuthenticationSettingsService.enableProvider(id))
        .then(() => {
          siteAuthenticationSettingsService.getSiteAuthenticationSettings().then((siteAuthenticationSettings: ISiteAuthenticationSettings) => {
            $scope.siteAuthenticationSettings = siteAuthenticationSettings;
            $scope.isExternalProviderEnabled = (id !== siteAuthenticationSettings.defaultProvider.id);
          });
        });
    };
    $scope.getHelp = (category?: string) => {
      const url = this.help.getServerHelpUrlByCategory(category);
      this.$window.open(url, '_blank');
    };
    $scope.getProviderDescription = (provider: IAuthenticationProvider) => {
      switch(provider.type) {
        case AuthenticationProviderType.oidc:
          return $translate.instant('ssoAuthTypeOIDC_description');
        case AuthenticationProviderType.saml:
          return $translate.instant('ssoAuthTypeSAML_description');
        case AuthenticationProviderType.default:
          return $translate.instant('ssoAuthTypeDefault_description');
      }
    };
    $scope.getProviderDisplayName = (provider: IAuthenticationProvider) => {
      return provider
        ? siteAuthenticationSettingsService.getProviderDisplayName(provider)
        : $translate.instant('siteAuthenticationTypeSSO');
    };
    $scope.isConnectedClientsVisible = server.isConnectedClientsVisible();
    $scope.isEmbeddedVizForSiteSamlSupported = server.isEmbeddedVizForSiteSamlSupported();
    $scope.isExternalProviderEnabled = !!_.find($scope.siteAuthenticationSettings.externalProviders, (provider: IAuthenticationProvider) => provider.isEnabled);
    $scope.isExternalProviderEnabledChanged = () => {
      const newVal = !$scope.isExternalProviderEnabled;
      const provider = (newVal ?
        $scope.siteAuthenticationSettings.externalProviders[0] :
        $scope.siteAuthenticationSettings.defaultProvider);
      $scope.enableProvider(provider.id).then(() => {
        $scope.isExternalProviderEnabled = newVal;
      });
    };
    $scope.isTableauIdSupported = server.isTableauIdSupported();
    $scope.openAddExternalUsersDialog = ($event: JQueryEventObject) => {
      const context = ActionContext.fromEvent($event);
      addUsersOptionsAction.showAddSiteUsersOptionsDialog(context);
    };
    $scope.siteAuthenticationSettings = $scope.siteAuthenticationSettings;
    $scope.siteSettingsForSiteAdmin =  $scope.siteSettingsForSiteAdmin;

    const urls = siteUrlsSelector(appStore.getState());
    $scope.usersUrl = urls.usersUrl();

    watchWithUndo($scope, 'siteAuthenticationSettings.providerIdForEmbeddedViews', (val: string) => {
      return this.notifyOnFail(siteAuthenticationSettingsService.setProviderIdForEmbeddedViews(val));
    });

    watchWithUndo($scope, 'siteSettingsForSiteAdmin', (val: ISiteSettingsForSiteAdmin) => {
      const params: IUpdateSiteSettingsForSiteAdminParams = {
          id: (val as any).id, // remove cast with tfs 792889
          siteAdminSettings: val.siteAdminSettings as ISiteAdminSettings,
          subscriptionSettings: val.subscriptionSettings
      };

      return this.notifyOnFail(server.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params)));
    }, true);
  }

  private showEnableProviderWarning(id: string): ng.IPromise<any> {
    return this.siteUsers.getById(this.server.sessionUserInfo().id).then((me: ISiteUser) => {
      const dialogScope = <IActionDialogSiteAuthenticationProviderEnableScope>this.$scope.$new();
      dialogScope.aboutToBeDisabled = _.filter(
        this.$scope.siteAuthenticationSettings.externalProviders,
        (provider: IAuthenticationProvider) => provider.isEnabled && provider.id !== id);
      dialogScope.aboutToLockMyselfOut = !!_.find(
        dialogScope.aboutToBeDisabled,
        (provider: IAuthenticationProvider) => !!me && provider.id === me.authenticationProviderId);

      if (dialogScope.aboutToBeDisabled.length === 0) {
        return this.$q.when();
      }

      return this.confirmActionDialog.open({
        action: () => undefined,
        actionName: this.$translate.instant('continueMessage'),
        title: this.$translate.instant('ssoBeforeYouContinue_title'),
        bodyTemplateUrl: 'confirmActionDialogSiteAuthenticationProviderEnable.html',
        scope: dialogScope,
        confirmDisabled: () => dialogScope.aboutToLockMyselfOut
      }).result;
    });
  }

  private notifyOnFail<TResult>(result: ng.IPromise<TResult>, translateKey?: string): ng.IPromise<TResult> {
    result.catch(() => {
      this.toaster.pop({ type: ToastType.Error, text: this.$translate.instant(translateKey || 'unexpectedError') });
    });
    return result;
  }
}

Module.instance().controller('SiteAuthenticationSettingsCtrl', SiteAuthenticationSettingsCtrl);
